'use client'

/**
 * Modal-stack-aware Escape handler (#7134 follow-up).
 *
 * Background. Every modal in the app used to attach its own
 * `document.addEventListener('keydown', …)` listener for the Escape
 * key, with `stopImmediatePropagation: true` (the #6280 fix). The
 * intent was: when modals stack, ESC closes only the topmost modal.
 *
 * The bug: `addEventListener` fires listeners in REGISTRATION order,
 * not LIFO. So when a user opens modal A first and Feedback second:
 *
 *   1. A's Escape handler registers first (handler A).
 *   2. Feedback's Escape handler registers second (handler F).
 *   3. ESC fires → A runs first → calls A's onClose →
 *      stopImmediatePropagation gates F → Feedback stays open.
 *
 * Modal A closes BEHIND the Feedback modal — exactly the opposite
 * of what the user expected.
 *
 * Fix. Replace the per-modal ESC listener with a single module-level
 * stack. Each open modal pushes its `close` callback on mount, pops
 * on unmount. A single global keydown listener — installed once when
 * the first modal pushes, removed when the last modal pops — pops
 * the topmost entry on ESC and calls its `close`.
 *
 * Cross-component coverage. The Modal primitive uses this for every
 * modal in the app. The meeting modal's full-screen wizard shell
 * (which doesn't use the Modal primitive) uses it too, so meetings
 * yield to nested modals (Feedback, ConfirmationDialog, command bar)
 * correctly.
 */

import { useEffect } from 'react'

interface StackEntry {
  /** Unique within the stack — used for find/remove. */
  id: number
  /** The modal's close callback. */
  close: () => void
}

const stack: StackEntry[] = []
let nextId = 1

/**
 * Observers of the stack's depth (#16841). Notified synchronously after
 * every push and pop. See `subscribeModalEscapeStack`.
 */
const depthListeners = new Set<(depth: number) => void>()

function notifyDepth() {
  for (const listener of [...depthListeners]) listener(stack.length)
}

/**
 * Module-level ESC listener — installed once when the stack
 * transitions empty→non-empty, removed when it goes back to empty.
 * Co-locating the install/uninstall with the stack push/pop avoids
 * burdening every consumer with bookkeeping.
 */
let documentListenerInstalled = false

function handleKeyDown(e: KeyboardEvent) {
  if (e.key !== 'Escape') return
  const top = stack[stack.length - 1]
  if (!top) return
  // Escape closes the topmost modal unconditionally: in an input or a
  // textarea that is the universal "close the modal" gesture, so those are
  // deliberately not exempted. A nested control that owns Escape for itself
  // (e.g. SearchableSelect's open dropdown) must call `stopPropagation()` in
  // its own handler so the event never reaches this listener — there is no
  // exemption list here (#12636).
  e.preventDefault()
  top.close()
}

function installListener() {
  if (documentListenerInstalled) return
  document.addEventListener('keydown', handleKeyDown)
  documentListenerInstalled = true
}

function uninstallListenerIfEmpty() {
  if (stack.length > 0) return
  if (!documentListenerInstalled) return
  document.removeEventListener('keydown', handleKeyDown)
  documentListenerInstalled = false
}

/**
 * Stack-aware Escape-to-close hook. Push the modal on mount (or when
 * `enabled` flips true), pop on unmount/disable. ESC closes whichever
 * modal sits on top of the stack — the most recently opened one.
 *
 * @param enabled  Whether this modal is currently open and should be
 *                 considered for Escape handling. Mirrors the
 *                 `useKeyboardShortcut(..., { enabled })` shape so
 *                 callers can pass `isOpen && !preventClose`.
 * @param close    The close callback — invoked when the user presses
 *                 Escape and this modal is the topmost stack entry.
 */
export function useModalEscape(enabled: boolean, close: () => void): void {
  useEffect(() => {
    if (!enabled) return
    const id = nextId++
    stack.push({ id, close })
    installListener()
    notifyDepth()
    return () => {
      const idx = stack.findIndex(e => e.id === id)
      if (idx >= 0) stack.splice(idx, 1)
      uninstallListenerIfEmpty()
      notifyDepth()
    }
    // We deliberately depend only on `enabled`. The `close` callback
    // is read at the time the effect mounts — re-binding on every
    // render would churn the stack on each keystroke (the close
    // callback identity often shifts because callers wrap onClose in
    // arrow functions). For the Escape-only use case this is fine: a
    // close callback that captures stale state is rare for modal UIs,
    // and the alternative (re-pushing on every render) is costlier.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])
}

/**
 * Whether any modal currently owns Escape (#13676).
 *
 * For a NON-modal region that also closes on Escape and deliberately stays
 * off this stack — Home's docked history panel is the first — there is no
 * other way to yield. `document` listeners fire in REGISTRATION order, so
 * such a region opened before a modal would run first and close itself on an
 * Escape meant for the dialog above it, which is the same ordering bug the
 * stack itself exists to fix, arriving from outside the stack.
 *
 * Read it, do not push onto the stack: pushing would make Escape close the
 * region INSTEAD of the dialog, which is the opposite failure.
 */
export function isModalEscapeStackActive(): boolean {
  return stack.length > 0
}

/** How many modals currently hold the stack (#16841). */
export function getModalEscapeStackDepth(): number {
  return stack.length
}

/**
 * Observe the stack's depth (#16841). The listener runs synchronously after
 * each push and pop with the new depth; the return value unsubscribes.
 *
 * The feedback element picker is the reader: its capture layer is drawn
 * ABOVE ordinary modals, so a dialog that opens while it is up would land
 * underneath it. Observing a push lets the picker step aside. Observe only —
 * like `isModalEscapeStackActive`, never push to learn the depth.
 */
export function subscribeModalEscapeStack(
  listener: (depth: number) => void,
): () => void {
  depthListeners.add(listener)
  return () => {
    depthListeners.delete(listener)
  }
}

/**
 * Test helper. Resets the module-level stack between tests so a leaky
 * test doesn't carry an entry into the next one. Not part of the
 * public API.
 */
export function __resetModalStackForTests(): void {
  stack.length = 0
  uninstallListenerIfEmpty()
}
